// src/hooks/useBCPModals.ts - Modal state management for BCP
import { useState, useCallback } from 'react';
import { AnalysisStruct } from '../services/api';

export interface BCPModalState {
  // AI Analysis Modal
  showAnalysisModal: boolean;
  setShowAnalysisModal: (show: boolean) => void;
  analysis: AnalysisStruct | null;
  setAnalysis: React.Dispatch<React.SetStateAction<AnalysisStruct | null>>;

  // Document Preview Modal
  showPreviewModal: boolean;
  setShowPreviewModal: (show: boolean) => void;
  previewBCPId: number | null;
  setPreviewBCPId: React.Dispatch<React.SetStateAction<number | null>>;
  previewBCPName: string;
  setPreviewBCPName: React.Dispatch<React.SetStateAction<string>>;

  // Subscription Upgrade Modal
  showUpgradeModal: boolean;
  setShowUpgradeModal: (show: boolean) => void;

  // BCP Generation Modal
  showGenerationModal: boolean;
  setShowGenerationModal: (show: boolean) => void;
  generationStep: 'saving' | 'generating' | 'complete';
  setGenerationStep: React.Dispatch<React.SetStateAction<'saving' | 'generating' | 'complete'>>;
  generatedBCPId: number | null;
  setGeneratedBCPId: React.Dispatch<React.SetStateAction<number | null>>;

  // Modal handlers
  handlePreviewBCP: (bcpId: number, bcpName: string) => void;
  handleShowAnalysis: (analysis: AnalysisStruct) => void;
  handleShowUpgrade: () => void;
  handleShowGeneration: () => void;
  handleCloseAllModals: () => void;
}

export const useBCPModals = (): BCPModalState => {
  // AI Analysis Modal
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysis, setAnalysis] = useState<AnalysisStruct | null>(null);

  // Document Preview Modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewBCPId, setPreviewBCPId] = useState<number | null>(null);
  const [previewBCPName, setPreviewBCPName] = useState<string>('');

  // Subscription Upgrade Modal
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // BCP Generation Modal
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [generationStep, setGenerationStep] = useState<'saving' | 'generating' | 'complete'>('saving');
  const [generatedBCPId, setGeneratedBCPId] = useState<number | null>(null);

  // Modal handlers
  const handlePreviewBCP = useCallback((bcpId: number, bcpName: string) => {
    setPreviewBCPId(bcpId);
    setPreviewBCPName(bcpName);
    setShowPreviewModal(true);
  }, []);

  const handleShowAnalysis = useCallback((analysisData: AnalysisStruct) => {
    setAnalysis(analysisData);
    setShowAnalysisModal(true);
  }, []);

  const handleShowUpgrade = useCallback(() => {
    setShowUpgradeModal(true);
  }, []);

  const handleShowGeneration = useCallback(() => {
    setShowGenerationModal(true);
    setGenerationStep('saving');
  }, []);

  const handleCloseAllModals = useCallback(() => {
    setShowAnalysisModal(false);
    setShowPreviewModal(false);
    setShowUpgradeModal(false);
    setShowGenerationModal(false);
  }, []);

  return {
    // AI Analysis Modal
    showAnalysisModal,
    setShowAnalysisModal,
    analysis,
    setAnalysis,

    // Document Preview Modal
    showPreviewModal,
    setShowPreviewModal,
    previewBCPId,
    setPreviewBCPId,
    previewBCPName,
    setPreviewBCPName,

    // Subscription Upgrade Modal
    showUpgradeModal,
    setShowUpgradeModal,

    // BCP Generation Modal
    showGenerationModal,
    setShowGenerationModal,
    generationStep,
    setGenerationStep,
    generatedBCPId,
    setGeneratedBCPId,

    // Modal handlers
    handlePreviewBCP,
    handleShowAnalysis,
    handleShowUpgrade,
    handleShowGeneration,
    handleCloseAllModals,
  };
};
